import { useCallback } from "react";
import * as React from "react";
import {
  Controller,
  DeepMap,
  FieldError,
  FieldErrors,
  FieldValues,
  UseControllerProps
} from "react-hook-form";

import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import type { SxProps } from "@mui/material/styles";

import { get } from "lodash";

import { DEFAULT_LABEL_CONFIG } from "app/themes/components/input";

export type OptionType = {
  value: any;
  label: string;
};

type ToggleButtonGroupFormFieldProps = {
  options: OptionType[];
  label: string;
  disabled?: boolean;
  required?: boolean;
  sx?: SxProps;
  optionSx?: SxProps;
  afterChange?: (value: string) => void;
};

export type RhfToggleButtonGroupFormFieldProps<T extends FieldValues> =
  ToggleButtonGroupFormFieldProps &
    UseControllerProps<T> & { errors?: FieldErrors<T> };

export default function ToggleButtonGroupFormField<T extends FieldValues>({
  name,
  label,
  disabled = false,
  control,
  options,
  required = false,
  rules,
  errors,
  afterChange,
  optionSx,
  sx
}: RhfToggleButtonGroupFormFieldProps<T>) {
  const validate = useCallback(
    (value: any) =>
      !required ||
      (required && value !== null && value !== undefined && value !== "")
        ? true
        : `${label || "Selection"} is required`,
    [required, label]
  );
  const errorMessage =
    errors &&
    get(errors, name) &&
    `${(get(errors, name) as DeepMap<FieldValues, FieldError>).message}`;
  return (
    <FormControl
      disabled={disabled}
      required={required}
      fullWidth
      error={Boolean(errorMessage)}
      sx={sx}
    >
      {Boolean(label) && (
        <FormLabel
          sx={DEFAULT_LABEL_CONFIG}
          style={{ paddingBottom: "4px" }}
          id={name}
        >
          {label}
        </FormLabel>
      )}
      {/* A caller-provided rules.validate REPLACES the built-in required
          check (spread order), so such a caller must handle emptiness itself.
          Other rule keys merge alongside it. */}
      <Controller
        rules={{ validate, ...rules }}
        control={control}
        name={name}
        render={({ field }) => (
          <ToggleButtonGroup
            size="small"
            exclusive
            {...field}
            value={field.value ?? ""}
            sx={optionSx}
            onChange={(event, value) => {
              field.onChange(value);
              if (afterChange) {
                afterChange(value);
              }
            }}
          >
            {options.map((option) => (
              <ToggleButton
                key={option.value}
                value={String(option.value)}
                sx={{ flex: 1 }}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      />
      {Boolean(errorMessage) && (
        <FormHelperText error={Boolean(errorMessage)}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  );
}
